import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import { reportError } from '@/lib/errorReporting';
import { Button } from '@/components/ui/Button';

function Fallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div role="alert" className="flex min-h-dvh items-center justify-center bg-bg p-6">
      <div className="panel flex max-w-md flex-col items-center gap-3 p-6 text-center">
        <TriangleAlert className="size-8 text-check" />
        <h1 className="text-lg font-semibold">{t('crash.title')}</h1>
        <p className="text-sm text-ink-2">{t('crash.body')}</p>
        <div className="flex gap-2">
          <Button variant="primary" icon={<RefreshCw className="size-4" />} onClick={() => window.location.reload()}>{t('crash.reload')}</Button>
          <Button onClick={onRetry}>{t('crash.retry')}</Button>
        </div>
      </div>
    </div>
  );
}

/** Shows a way out instead of a blank page when a screen fails to draw, and reports the error to the platform admins. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, { componentStack: info.componentStack });
  }

  render() {
    return this.state.failed ? <Fallback onRetry={() => this.setState({ failed: false })} /> : this.props.children;
  }
}
